import axios from 'axios';

const getOrGenerateDeviceId = () => {
	let deviceId = localStorage.getItem('deviceId');
	if (!deviceId) {
		deviceId = window.crypto.randomUUID ? window.crypto.randomUUID() : 'manual-fallback-uuid-' + Date.now();
		localStorage.setItem('deviceId', deviceId);
	}
	return deviceId;
};

const clearProviderSession = () => {
	localStorage.removeItem('accessToken');
	localStorage.removeItem('refreshToken');
};

const api = axios.create({
	baseURL: process.env.REACT_APP_API_BASE_URL || '',
	headers: { 'Content-Type': 'application/json' }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
	failedQueue.forEach(prom => {
		if (error) prom.reject(error);
		else prom.resolve(token);
	});
	failedQueue = [];
};

api.interceptors.request.use((config) => {
	const token = localStorage.getItem('accessToken');
	if (token && !config.url?.includes('/api/v1/auth/refresh')) {
		config.headers.Authorization = `Bearer ${token}`;
	}

	const deviceId = getOrGenerateDeviceId();
	if (deviceId) {
		config.headers['X-Device-Id'] = deviceId;
	}

	if (config.data instanceof FormData) {
		delete config.headers['Content-Type'];
	}

	return config;
});

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		if (!originalRequest || error.response?.status !== 401) {
			return Promise.reject(error);
		}

		if (originalRequest._retry || originalRequest.url?.includes('/refresh')) {
			return Promise.reject(error);
		}

		const refreshToken = localStorage.getItem('refreshToken');
		if (!refreshToken) {
			return Promise.reject(error);
		}

		if (isRefreshing) {
			return new Promise((resolve, reject) => {
				failedQueue.push({ resolve, reject });
			})
				.then(token => {
					originalRequest.headers.Authorization = `Bearer ${token}`;
					return api(originalRequest);
				})
				.catch(err => Promise.reject(err));
		}

		originalRequest._retry = true;
		isRefreshing = true;

		try {
			const deviceId = getOrGenerateDeviceId();
			const res = await axios.post('/api/v1/auth/refresh', {}, {
				headers: {
					'Refresh-Token': `Bearer ${refreshToken}`,
					'X-Device-Id': deviceId
				}
			});

			const newToken = res.data.accessToken;
			localStorage.setItem('accessToken', newToken);
			if (res.data.refreshToken) {
				localStorage.setItem('refreshToken', res.data.refreshToken);
			}

			processQueue(null, newToken);
			originalRequest.headers.Authorization = `Bearer ${newToken}`;
			return api(originalRequest);
		} catch (refreshError) {
			processQueue(refreshError, null);
			clearProviderSession();
			if (!window.location.pathname.includes('/login')) {
				window.location.href = '/login';
			}
			return Promise.reject(refreshError);
		} finally {
			isRefreshing = false;
		}
	}
);

export default api;
